import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControlLabel,
  Link as MuiLink,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { GppGood, OpenInNew } from '@mui/icons-material';
import { acceptTerms, fetchTermsStatus } from '../hooks/hooks';

/**
 * Blocks the dashboard until the current terms are accepted.
 *
 * The dialog cannot be dismissed — no backdrop click, no escape key, no close
 * button. Acceptance is the only way past it, because a record that someone
 * merely saw the terms is not worth having.
 *
 * If the status call fails we let the user through rather than locking them out
 * of their money over a network error. A missed acceptance is recoverable; a
 * merchant unable to reach their balance is not.
 */
const TermsGate = ({ children }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const queryClient = useQueryClient();
  const [checked, setChecked] = useState(false);
  const [error, setError] = useState(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['termsStatus'],
    queryFn: fetchTermsStatus,
    retry: 1,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  const mutation = useMutation({
    mutationFn: () => acceptTerms(data?.current_version),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['termsStatus'] }),
    onError: (err) => setError(
      err?.response?.data?.message || 'Could not record your acceptance. Please try again.'
    ),
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
                 minHeight: '60vh' }}>
        <CircularProgress size={26} />
      </Box>
    );
  }

  // Fail open — see the note above.
  if (isError || data?.accepted) return children;

  const isUpdate = Boolean(data?.previously_accepted_version);

  return (
    <>
      {children}
      <Dialog
        open
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
        onClose={(_e, reason) => {
          // Ignore every dismissal route.
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        }}
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <Box sx={{
          p: 3, color: '#fff',
          background: 'linear-gradient(140deg, #0c1a14 0%, #123d2b 45%, #0d5c40 100%)',
        }}>
          <Stack direction="row" alignItems="center" gap={1} mb={1}>
            <GppGood sx={{ fontSize: 20 }} />
            <Typography fontSize={12} fontWeight={700} letterSpacing={1.2}>
              {isUpdate ? 'UPDATED TERMS' : 'BEFORE YOU CONTINUE'}
            </Typography>
          </Stack>
          <Typography fontSize={21} fontWeight={800} lineHeight={1.25}>
            {isUpdate
              ? 'We have updated our Terms of Service'
              : 'Please accept our Terms of Service'}
          </Typography>
          <Typography fontSize={13.5} sx={{ color: 'rgba(255,255,255,0.78)', mt: 1 }}>
            Version {data?.current_version}
            {data?.effective_date ? ` · effective ${data.effective_date}` : ''}
          </Typography>
        </Box>

        <DialogContent sx={{ pt: 2.5 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2, fontSize: 13 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Typography fontSize={13.5} color="text.secondary" mb={2}>
            {isUpdate
              ? 'The points that changed most are below. Please read the full terms before accepting.'
              : 'These are the points worth knowing before you continue. Please read the full terms before accepting.'}
          </Typography>

          <Stack gap={1.5} sx={{ mb: 2.5 }}>
            {[
              ['ZenoPay is not a bank',
               'We are a technology company. Your money is held by licensed partners, not by us, and balances are not bank deposits.'],
              ['Verification is required',
               'KYC for personal accounts, KYB for businesses. Accounts that are not verified cannot transact.'],
              ['Partners can be slow or unavailable',
               'Mobile money and card partners have outages and maintenance. Transactions may be delayed — do not retry a stuck payment.'],
              ['Funds can be held during a review',
               'Where fraud is suspected, funds may be held and reviewed. A partner investigation can take up to twelve months.'],
              ['Accounts can be suspended',
               'For breach of these terms, our partners’ rules, or where we detect fraud. Repeated fraud means closure without warning.'],
            ].map(([title, detail]) => (
              <Stack key={title} direction="row" gap={1.25} alignItems="flex-start">
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#0BA480',
                           mt: '7px', flexShrink: 0 }} />
                <Typography fontSize={13.5} lineHeight={1.6} color="text.secondary">
                  <strong style={{ color: theme.palette.text.primary }}>{title}.</strong> {detail}
                </Typography>
              </Stack>
            ))}
          </Stack>

          <Divider sx={{ mb: 2 }} />

          <MuiLink
            href="/terms" target="_blank" rel="noopener noreferrer"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5,
                  color: '#0BA480', fontSize: 14, fontWeight: 600, textDecoration: 'none' }}
          >
            Read the full Terms of Service
            <OpenInNew sx={{ fontSize: 15 }} />
          </MuiLink>

          <FormControlLabel
            sx={{ mt: 1.5, alignItems: 'flex-start' }}
            control={
              <Checkbox
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
                sx={{ pt: 0.25, color: '#0BA480', '&.Mui-checked': { color: '#0BA480' } }}
              />
            }
            label={
              <Typography fontSize={13.5} color="text.secondary">
                I have read and accept the Terms of Service, and I confirm I am authorised
                to accept them for this account.
              </Typography>
            }
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 0 }}>
          <Button
            fullWidth
            variant="contained"
            disabled={!checked || mutation.isPending}
            onClick={() => { setError(null); mutation.mutate(); }}
            sx={{ py: 1.3, borderRadius: '12px', textTransform: 'none',
                  fontSize: 15, fontWeight: 700, boxShadow: 0, backgroundColor: '#0BA480',
                  '&:hover': { boxShadow: 'none', backgroundColor: '#0A8F70' } }}
          >
            {mutation.isPending ? 'Saving…' : 'Accept and continue'}
          </Button>
        </DialogActions>

        <Typography
          fontSize={11.5}
          color="text.secondary"
          sx={{ textAlign: 'center', pb: 2.5, px: 3,
                bgcolor: isDark ? 'transparent' : undefined }}
        >
          Questions? Email support@zeno.africa or call +255 793 166 166.
        </Typography>
      </Dialog>
    </>
  );
};

export default TermsGate;
